// i18n runtime (FE-006) — a hand-rolled, zero-dependency locale layer. The whole
// mechanism is a lookup with an `en` fallback and Intl-driven pluralization; no
// library, so it costs the 30 kB gzip budget only the `en` strings the app
// already shipped plus ~1 kB of runtime.
//
// Catalogue values are rendered as TEXT (callers assign to textContent), never
// HTML — a translation string can therefore never become an injection vector.
//
// `en` is the SOURCE catalogue: MessageKey is `keyof typeof en`, so a missing or
// mistyped key is a compile error (tsc, in `npm run lint`). That structurally
// guarantees FE-006.3's "en always has the key", so a lookup never renders a raw
// key or an empty string.

import { en } from './en';
// Type-only import of the admin slice (issue 182 / 94a). `import type` is erased at
// compile time, so this pulls the admin key SHAPE into MessageKey WITHOUT emitting a
// runtime import — the admin values ship in their own lazy chunk (en.admin.ts),
// loaded by ensureAdminI18nLoaded(), never in main.js. Because the type still covers
// admin.*, t()/tp() keep type-checking those keys at compile time.
import type { enAdmin } from './en.admin';
// Type-only import of the driver trip-mode slice (GPS-011) — the same
// shape-without-values mechanism as the admin slice: keys type-check
// eagerly, values load with the /trip route via ensureDriverI18nLoaded().
import type { enDriver } from './en.driver';
// Type-only import of the ai.* slice (FE-002): the review/confirm screen's keys
// type-check eagerly; values load with the ingest route via ensureAiI18nLoaded().
import type { enAi } from './en.ai';
// Type-only import of the catalogs.* slice (issue 671): the catalog admin's
// `admin.catalogs.*` keys type-check eagerly; values load with /admin/catalogs
// via ensureCatalogsI18nLoaded() (i18n/catalogsSlice.ts, in the page chunk).
// Split out of the admin slice because the es/pt admin chunks had reached the
// 29 kB per-file budget.
import type { enCatalogs } from './en.catalogs';
// Type-only import of the fleet.* slice (QA sweep 2026-10b): the fleet lens's
// `adminFleet.*` keys, values loaded with /admin/fleet via ensureFleetI18nLoaded()
// (i18n/fleetSlice.ts, in the page chunk) — split out when the es admin chunk
// crossed the 29 kB per-file budget.
import type { enFleet } from './en.fleet';

// Fexo targets en/es/pt (English, Spanish, Portuguese). Only `en` carries
// content today (FE-006 delivers the mechanism, not the translations); `es`/`pt`
// are resolvable so the seam and the fallback are real and testable — their
// catalogues are empty, so every key falls back to `en` (FE-006.3).
export const SUPPORTED_LOCALES = ['en', 'es', 'pt'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

// CLDR plural categories. `pt` (and `es`) use one/many/other — `many` covers the
// 10⁶-magnitude forms — which is exactly why a count noun cannot be a naive
// `n === 1` ternary (FE-006.4). Kept as our own type rather than
// Intl.LDMLPluralRule so the catalogue's shape does not depend on the ambient
// lib version.
export type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';
export type PluralForms = Partial<Record<PluralCategory, string>>;

// The full key namespace = the eager `en` source ∪ the lazy `admin.*` and
// `trip.*` slices. The intersection is a pure type operation (both slice
// imports are type-only), so MessageKey covers every lazy key even though
// those values are not in the eager bundle.
type Catalog = typeof en & typeof enAdmin & typeof enDriver & typeof enAi & typeof enCatalogs & typeof enFleet;
export type MessageKey = keyof Catalog;

// StringKey / PluralKey split the catalogue by value shape so t() only accepts
// plain-string entries and tp() only accepts plural entries — a count noun
// passed to t(), or a sentence passed to tp(), is a compile error.
export type StringKey = { [K in MessageKey]: Catalog[K] extends string ? K : never }[MessageKey];
export type PluralKey = { [K in MessageKey]: Catalog[K] extends PluralForms ? K : never }[MessageKey];

export type Params = Record<string, string | number>;

const STORAGE_KEY = 'fexo.locale';

const isSupported = (v: string): v is Locale => (SUPPORTED_LOCALES as readonly string[]).includes(v);

// A non-en locale may translate any subset of keys and — for a plural entry —
// may carry more CLDR categories than `en` does (es/pt add `many` where en has
// only one/other). So a locale catalogue is looser than the `en` source: any
// key, a string or a full PluralForms.
export type LocaleCatalog = Partial<Record<MessageKey, string | PluralForms>>;

// Per-locale catalogues. `en` is the full source; `es`/`pt` start empty and can
// be filled (or registered) when their translations land — until then they fall
// back to `en` entry-by-entry.
const catalogs: Record<Locale, LocaleCatalog> = { en, es: {}, pt: {} };

let activeLocale: Locale = 'en';

export function getLocale(): Locale {
  return activeLocale;
}

// registerCatalog merges a locale's translations in. The extension seam for
// es/pt when their content lands (and how tests exercise a non-en locale without
// shipping es/pt UI copy).
export function registerCatalog(locale: Locale, catalog: LocaleCatalog): void {
  catalogs[locale] = { ...catalogs[locale], ...catalog };
}

// The es/pt catalogues ship as their own lazily-imported chunks (issue 114) — two full
// catalogues inline would blow main.js past its gzip budget (~+20 kB). Each loader
// dynamic-imports its chunk and registers it; Vite emits one chunk per locale.
const CATALOG_LOADERS: Partial<Record<Locale, () => Promise<void>>> = {
  es: () => import('./es').then((m) => registerCatalog('es', m.es)),
  pt: () => import('./pt').then((m) => registerCatalog('pt', m.pt)),
};

// `en` is built in; a locale whose chunk is already registered needs nothing.
const loaded = new Set<Locale>(['en']);
const inflight: Partial<Record<Locale, Promise<void>>> = {};

// ensureCatalogLoaded loads a locale's translation chunk on demand and resolves
// once its content is registered (so a subsequent t()/tp() renders in-language,
// not via the en fallback). Idempotent, and it de-dupes concurrent callers via an
// in-flight map so the boot-load racing an in-menu switch imports the chunk once.
// A locale with no chunk (en) resolves immediately; a failed import clears the
// slot so a later attempt can retry, and leaves the locale on the en fallback
// rather than breaking the app (FE-006.3).
export function ensureCatalogLoaded(locale: Locale): Promise<void> {
  if (loaded.has(locale)) return Promise.resolve();
  const loader = CATALOG_LOADERS[locale];
  if (!loader) return Promise.resolve();
  const pending = inflight[locale];
  if (pending) return pending;
  const load = loader()
    .then(() => {
      loaded.add(locale);
      delete inflight[locale];
    })
    .catch((err: unknown) => {
      delete inflight[locale];
      throw err;
    });
  inflight[locale] = load;
  return load;
}

// ── Lazy i18n slices (admin: issue 182 / 94a · driver: GPS-011) ───────────────
// A slice's keys live in per-locale chunks (en.<slice>.ts etc.) so their values
// stay out of main.js; the owning route awaits its ensure*() before mounting, so
// no sliced key renders a missing-key fallback. sliceLoader generalizes the
// machinery once: per-locale de-dupe + in-flight maps (a route mount racing a
// locale switch imports each chunk once; a failed import clears its slot so a
// later attempt can retry), and every ensure*() loads the ACTIVE locale AND the
// en baseline the fallback reads through. On a locale switch the route re-mounts
// (LOCALE_CHANGED_EVENT) and calls its ensure*() again, so slices follow the
// language.
// Exported for a slice whose loader lives beside its one consumer rather
// than here (i18n/catalogsSlice.ts, issue 671): the core chunk rides every
// first load, so a loader only one lazy page needs is defined in that page's
// chunk and this module carries no bytes for it.
export function sliceLoader(loaders: Record<Locale, () => Promise<LocaleCatalog>>): () => Promise<void> {
  const done = new Set<Locale>();
  const inflight: Partial<Record<Locale, Promise<void>>> = {};
  const loadFor = (locale: Locale): Promise<void> => {
    if (done.has(locale)) return Promise.resolve();
    const pending = inflight[locale];
    if (pending) return pending;
    const load = loaders[locale]()
      .then((catalog) => {
        registerCatalog(locale, catalog);
        done.add(locale);
        delete inflight[locale];
      })
      .catch((err: unknown) => {
        delete inflight[locale];
        throw err;
      });
    inflight[locale] = load;
    return load;
  };
  return () => {
    const locale = getLocale();
    const needed = locale === 'en' ? [loadFor('en')] : [loadFor('en'), loadFor(locale)];
    return Promise.all(needed).then(() => undefined);
  };
}

export const ensureAdminI18nLoaded = sliceLoader({
  en: () => import('./en.admin').then((m) => m.enAdmin),
  es: () => import('./es.admin').then((m) => m.esAdmin),
  pt: () => import('./pt.admin').then((m) => m.ptAdmin),
});

// The driver trip.* slice: es.driver.ts is the AUTHORED CANON for the trip-mode
// surface (driver-app-spec DRV7); en stays the type source + runtime fallback.
export const ensureDriverI18nLoaded = sliceLoader({
  en: () => import('./en.driver').then((m) => m.enDriver),
  es: () => import('./es.driver').then((m) => m.esDriver),
  pt: () => import('./pt.driver').then((m) => m.ptDriver),
});

// The ai.* slice (FE-002 / ai-ingestion-spec §7): es.ai.ts is the AUTHORED
// CANON (FE-I.6 — es first); en stays the type source + runtime fallback.
export const ensureAiI18nLoaded = sliceLoader({
  en: () => import('./en.ai').then((m) => m.enAi),
  es: () => import('./es.ai').then((m) => m.esAi),
  pt: () => import('./pt.ai').then((m) => m.ptAi),
});

// entry resolves a key against the active locale, then the en baseline (FE-006.3).
// The fallback reads the runtime `catalogs.en`, not the static `en` import: the
// admin.* slice registers into catalogs.en on load (ensureAdminI18nLoaded), so an
// admin key resolves once its route has awaited the slice. For an eager key,
// catalogs.en === the en source, so this is unchanged. An admin key looked up before
// its slice loads resolves undefined — which is exactly why admin routes await first.
function entry<K extends MessageKey>(key: K): Catalog[K] {
  const active = catalogs[activeLocale];
  return (active[key] ?? catalogs.en[key]) as Catalog[K];
}

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
  );
}

// t returns a localized sentence, with {name} placeholders filled from params.
export function t(key: StringKey, params?: Params): string {
  return interpolate(entry(key) as string, params);
}

// pluralCategory is the count → CLDR category selection for the active locale —
// exposed so the pluralization contract (not a `n === 1` ternary) is directly
// testable (FE-006.4).
export function pluralCategory(n: number, locale: Locale = activeLocale): PluralCategory {
  return new Intl.PluralRules(locale).select(n) as PluralCategory;
}

// tp returns a localized count noun. It selects the plural form for the active
// locale via Intl.PluralRules (FE-006.4), falling back within the entry to
// `other`, then to the `en` entry, then to the `en` `other` form. `{n}` is
// interpolated automatically alongside any extra params.
export function tp(key: PluralKey, n: number, params?: Params): string {
  const forms = entry(key) as PluralForms;
  // en baseline via the runtime catalog (see entry()): covers admin.* plural keys
  // once their slice is registered, and equals the en source for eager keys.
  const enForms = catalogs.en[key] as PluralForms;
  const cat = pluralCategory(n);
  const template = forms[cat] ?? forms.other ?? enForms[cat] ?? enForms.other ?? '';
  return interpolate(template, { n, ...params });
}

// LOCALE_CHANGED_EVENT fires when an explicit user choice (setLocale with
// { persist: true }) actually changes the active locale. The app shell listens
// and re-renders the current view, so an in-menu language switch lands without a
// page reload (FE-010.3). Only a persisted, real change fires it: the import-time
// resolution below and initLocale() pass no `persist`, so nothing is emitted
// before the app's listener is wired. Mirrors RBAC_CONTEXT_EVENT (capabilities.ts).
export const LOCALE_CHANGED_EVENT = 'app:locale-changed';

// setLocale switches the active locale and keeps <html lang> in sync (FE-006.2).
// An unsupported value is coerced to `en` rather than trusted. Pass
// { persist: true } for an explicit user choice (an in-app picker) so it
// survives a reload via localStorage and re-renders the app (FE-010.3).
export function setLocale(locale: Locale, opts?: { persist?: boolean }): void {
  const next = isSupported(locale) ? locale : 'en';
  const changed = next !== activeLocale;
  activeLocale = next;
  if (typeof document !== 'undefined') document.documentElement.lang = activeLocale;
  if (opts?.persist) {
    try {
      localStorage.setItem(STORAGE_KEY, activeLocale);
    } catch {
      // localStorage can throw (private mode, disabled storage); a failed
      // persist is not fatal — the choice simply won't survive the reload.
    }
    // An explicit, persisted choice that genuinely changed the locale re-renders
    // the app (FE-010.3). Guarded to a real change so re-picking the current
    // locale is a no-op, and to a browser context for jsdom/SSR safety.
    if (changed && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<Locale>(LOCALE_CHANGED_EVENT, { detail: activeLocale }));
    }
  }
}

// hasStoredLocalePreference reports whether the user (this browser) has an
// explicit locale choice. The driver trip route uses it for the DRV7
// default: no stored preference ⇒ the surface opens in es (the operational
// language) rather than the navigator guess.
export function hasStoredLocalePreference(): boolean {
  return storedPreference() !== null;
}

function storedPreference(): Locale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && isSupported(v) ? v : null;
  } catch {
    return null;
  }
}

function fromNavigator(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const lang of langs) {
    const base = (lang || '').toLowerCase().split('-')[0];
    if (isSupported(base)) return base;
  }
  return null;
}

// resolveLocale implements FE-006.2's precedence: user preference → navigator
// language → en. `preferred` is the server-side preference (getMe.locale); an
// in-app picker choice persisted to localStorage is the second rung of "user
// preference".
export function resolveLocale(preferred?: string | null): Locale {
  if (preferred) {
    const base = preferred.toLowerCase().split('-')[0];
    if (isSupported(base)) return base;
  }
  return storedPreference() ?? fromNavigator() ?? 'en';
}

// initLocale resolves and applies the active locale on boot. Called from app.ts
// after authInit (so getMe.locale can feed it) and before the router renders any
// page. Returns the resolved locale.
export function initLocale(preferred?: string | null): Locale {
  const locale = resolveLocale(preferred);
  setLocale(locale);
  return locale;
}

// Resolve the locale synchronously at module load (localStorage → navigator →
// en). Any module that renders a string imports this one, and a module's imports
// evaluate before its own body — so this runs before module-level t() constants
// (e.g. navModel) evaluate, and they capture the right locale even though they
// resolve before app.ts's post-authInit initLocale() runs. The server-side
// preference (getMe.locale) is folded in later by initLocale(); it has no write
// path yet, so this import-time resolution is already complete for today.
setLocale(resolveLocale(null));
